import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Grid, Container, Card } from "@mui/material";
import {
  PlayArrow as PlayIcon,
  ArrowForward as ArrowRightIcon,
} from "@mui/icons-material";

export default function BingoLandingPage() {
  const [animatedNumbers, setAnimatedNumbers] = useState([]);
  const [throwingBalls, setThrowingBalls] = useState([]);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    const numbers = Array.from(
      { length: 15 },
      () => Math.floor(Math.random() * 75) + 1
    );
    setAnimatedNumbers(numbers);

    const balls = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      number: Math.floor(Math.random() * 75) + 1,
      delay: i * 6,
      duration: 3 + Math.random() * 2,
      startX: Math.random() * 100,
      endX: Math.random() * 100,
      color: ["#ef4444", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6"][
        Math.floor(Math.random() * 5)
      ],
    }));
    setThrowingBalls(balls);
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "white",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden" }}>
        {throwingBalls.map((ball) => (
          <Box
            key={ball.id}
            sx={{
              position: "absolute",
              width: 40,
              height: 40,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontWeight: "bold",
              fontSize: "12px",
              bgcolor: ball.color,
              boxShadow: 4,
              left: `${ball.startX}%`,
              top: "-50px",
              animation: `throwBall ${ball.duration}s infinite linear`,
              animationDelay: `${ball.delay}s`,
              opacity: 0.8,
            }}
          >
            {ball.number}
          </Box>
        ))}
      </Box>

      <Container
        maxWidth="lg"
        sx={{ position: "relative", zIndex: 10, py: { xs: 4, lg: 8 } }}
      >
        <Grid container spacing={6} alignItems="center" sx={{ mb: 8 }}>
          <Grid item xs={12} lg={6}>
            <Box
              sx={{
                transform: isVisible ? "translateX(0)" : "translateX(-40px)",
                opacity: isVisible ? 1 : 0,
                transition: "all 1s ease-in-out",
                transitionDelay: "0.7s",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: "black",
                  fontWeight: 900,
                  lineHeight: 1.2,
                  fontSize: { xs: "2rem", lg: "3rem" },
                }}
              >
                Join the fun —<br />
                win big, play loud!
              </Typography>

              <Box
                sx={{
                  mb: 2,
                  p: 4,
                }}
              >
                <Typography
                  sx={{
                    color: "Black",
                    fontSize: "1.125rem",
                    lineHeight: 1.6,
                    mb: 1,
                  }}
                >
                  Bingo Social brings teams and audiences together with an
                  interactive bingo game — perfect for team bonding,
                  recognition, and good vibes.
                </Typography>
                <Typography
                  sx={{
                    color: "black",
                    fontSize: "1rem",
                    lineHeight: 1.6,
                  }}
                >
                  To create a fun, inclusive, and low-barrier activity that
                  boosts employee morale, encourages interaction across teams,
                  and reinforces a culture of positivity and collaboration.
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mt: 2,
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PlayIcon />}
                  sx={{
                    background:
                      "linear-gradient(45deg,rgb(231, 61, 247) 0%,rgb(201, 127, 211) 100%)",
                    boxShadow: "0 12px 20px -6px rgba(101, 69, 218, 0.35)",
                    color: "white",
                    fontWeight: "bold",
                    py: 1,
                    px: 2,
                    borderRadius: "50px",
                    fontSize: "14px",
                    minWidth: "180px",
                    "&:hover": {
                      background:
                        "linear-gradient(45deg,rgb(150, 70, 196),rgb(141, 68, 170))",
                      transform: "scale(1.05)",
                      boxShadow: 10,
                    },
                    transition: "all 0.3s ease-in-out",
                  }}
                  onClick={() => window.open("/organisers", "_self")}
                >
                  Let’s Bingo!
                </Button>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Box
              sx={{
                transform: isVisible ? "translateX(0)" : "translateX(40px)",
                opacity: isVisible ? 1 : 0,
                transition: "all 1s ease-in-out",
                transitionDelay: "0.9s",
                position: "relative",
              }}
            >
              <Card
                sx={{
                  backdropFilter: "blur(20px)",
                  borderRadius: 6,
                  p: 3,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: 4,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src="https://media1.giphy.com/media/v1.Y2lkPTc5MGI3NjExOGNlcTl2MmQ2ejZoZG1vaG1kZmVkaG5heWY5d2Vvc2c2cndjM2VqcSZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/VJreiexbHYEl0Uh9Kb/giphy.gif"
                    alt="Bingo Game Animation"
                    sx={{
                      width: "100%",
                      height: "500px",
                      display: "block",
                      borderRadius: 4,
                    }}
                  />

                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)",
                      animation: "shimmer 3s infinite",
                    }}
                  />
                </Box>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <style>
        {`
          @keyframes throwBall {
            0% {
              transform: translateY(0px) translateX(0px) rotate(0deg);
              opacity: 0.8;
            }
            25% {
              transform: translateY(200px) translateX(50px) rotate(90deg);
              opacity: 1;
            }
            50% {
              transform: translateY(400px) translateX(-30px) rotate(180deg);
              opacity: 1;
            }
            75% {
              transform: translateY(600px) translateX(80px) rotate(270deg);
              opacity: 0.8;
            }
            100% {
              transform: translateY(800px) translateX(0px) rotate(360deg);
              opacity: 0;
            }
          }
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
        `}
      </style>
    </Box>
  );
}
